import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Operational Quiet badges: a neutral-gray pill with a colored dot prefix and
 * foreground text. The semantic color appears ONLY in the dot — the rest of
 * the badge stays calm. This keeps tables readable across many rows.
 *
 * - `default` / `outline` / `secondary` / `muted` — gray dot, neutral text
 * - `success` / `warning` / `destructive` / `info` / `accent` / `soft` —
 *   colored dot, foreground text, neutral background
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border border-transparent bg-muted px-2 py-0.5 text-[11px] font-medium tracking-tight text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 before:h-1.5 before:w-1.5 before:rounded-full',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground before:hidden',
        secondary: 'before:bg-muted-foreground/50',
        outline: 'border-border bg-transparent before:bg-muted-foreground/50',
        muted: 'before:bg-muted-foreground/50',
        success: 'before:bg-success',
        warning: 'before:bg-warning',
        destructive: 'before:bg-destructive',
        info: 'before:bg-info',
        accent: 'before:bg-accent',
        soft: 'before:bg-primary',
      },
    },
    defaultVariants: {
      variant: 'muted',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
